import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, Button } from 'react-bootstrap';
import Layout from '../../../app/Layout';
import FormularioFiltroReporte from '../../components/ReporteNaranja/FormularioFiltroReporteNaranja';
import Breadcrumb from '../General/Breadcrumb';
import HeaderContent from '../General/HeaderContent';
import SpinnerLoading from '../../components/Spinners/SpinnerLoading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDollarSign, faStar, faUsers, faClipboardCheck,
  faChevronDown, faChevronUp,
  faExclamationTriangle, faCheckCircle, faTimesCircle,
  faChartBar, faFileExcel, faFilePdf, faExpand,
} from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { limpiarResultadoBusqueda, obtenerReporteNaranja } from '../../../actions/finanzasReportesActions';
import { obtenerProyectosActivos } from '../../../actions/cursosActions';
import { obtenerKpisAcademicos } from '../../../actions/kpisAcademicosActions';
import ResultadoRecaudacion from './resultadoRecaudacionVr2';
import ResultadoCalidad from './resultadoCalidad';
import ResultadoFidelidad from './resultadoFidelizacion';

// ── helpers ──────────────────────────────────────────────────────────────────
const safePct = v => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const META = 90;

const colorSem = v => v >= META ? '#28a745' : v >= 80 ? '#fd7e14' : '#dc3545';
const labelSem = v => v >= META ? 'Cumple' : v >= 80 ? 'En riesgo' : 'No cumple';
const iconSem  = v => v >= META ? faCheckCircle : v >= 80 ? faExclamationTriangle : faTimesCircle;

const BLOQUES = ['Recaudacion', 'Calidad', 'Retencion', 'Recursos'];
const BLOQUE_LABEL = {
  Recaudacion: 'Recaudación',
  Calidad:     'Calidad',
  Retencion:   'Retención',
  Recursos:    'Recursos 3 días',
};

// ── SummaryCard ───────────────────────────────────────────────────────────────
const SummaryCard = ({ titulo, valor, icon, subtitulo, detalle }) => {
  const v   = safePct(valor);
  const col = colorSem(v);
  const bar = Math.min(100, Math.round((v / META) * 100));
  return (
    <div className="col-md-3 col-sm-6 mb-3">
      <div className="box p-3 h-100" style={{ borderTop: `4px solid ${col}` }}>
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div style={{
            width: 40, height: 40, borderRadius: 8, flexShrink: 0,
            backgroundColor: `${col}20`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <FontAwesomeIcon icon={icon} style={{ color: col, fontSize: 18 }} />
          </div>
          <FontAwesomeIcon icon={iconSem(v)} style={{ color: col, fontSize: 18 }} />
        </div>
        <h2 style={{ color: col, fontWeight: 700, fontSize: 34, marginBottom: 2 }}>{v}%</h2>
        <p className="mb-1" style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>{titulo}</p>
        <div className="progress mb-1" style={{ height: 6, borderRadius: 3 }}>
          <div
            className="progress-bar"
            style={{ width: `${bar}%`, backgroundColor: col, borderRadius: 3, transition: 'width .5s ease' }}
          />
        </div>
        <small style={{ color: col, fontSize: 11 }}>
          Meta: {META}% — {v >= META ? 'Cumplida ✓' : `Falta ${(META - v).toFixed(1)}%`}
        </small>
        {detalle && (
          <small className="d-block mt-1" style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>{detalle}</small>
        )}
        {subtitulo && (
          <small className="text-muted d-block mt-1" style={{ fontSize: 11 }}>{subtitulo}</small>
        )}
      </div>
    </div>
  );
};

// ── BloqueKpi ─────────────────────────────────────────────────────────────────
const BloqueKpi = ({ titulo, valor, icon, children, onVerMas, descripcion }) => {
  const [open, setOpen] = useState(true);
  const v   = safePct(valor);
  const col = colorSem(v);
  return (
    <div className="box mb-3" style={{ overflow: 'hidden' }}>
      <div
        style={{
          padding: '12px 16px', borderBottom: open ? '1px solid #e9ecef' : 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <div
          onClick={() => setOpen(o => !o)}
          style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', flex: 1 }}
        >
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            backgroundColor: `${col}15`, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <FontAwesomeIcon icon={icon} style={{ color: col, fontSize: 16 }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{titulo}</div>
            {descripcion && <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>{descripcion}</div>}
            <div style={{ fontSize: 11, color: '#bbb', marginTop: 1 }}>Meta: {META}%</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 700, fontSize: 22, color: col }}>{v}%</span>
          <span style={{
            fontSize: 12, fontWeight: 600, color: col,
            backgroundColor: `${col}15`, padding: '3px 12px', borderRadius: 20,
          }}>
            {labelSem(v)}
          </span>
          {onVerMas && (
            <button
              className="btn btn-sm btn-outline-primary"
              style={{ fontSize: 12, padding: '3px 10px' }}
              onClick={e => { e.stopPropagation(); onVerMas(); }}
            >
              <FontAwesomeIcon icon={faExpand} className="mr-1" />
              Ver más
            </button>
          )}
          <FontAwesomeIcon
            icon={open ? faChevronUp : faChevronDown}
            onClick={() => setOpen(o => !o)}
            style={{ color: '#bbb', fontSize: 14, cursor: 'pointer' }}
          />
        </div>
      </div>
      {open && <div className="p-3">{children}</div>}
    </div>
  );
};

// ── RecursosBlock ─────────────────────────────────────────────────────────────
const RecursosBlock = ({ recursos }) => {
  if (!recursos || recursos.length === 0)
    return (
      <p className="text-muted mb-0">
        Sin datos de recursos para el mes actual. Asegúrate de que existan sesiones registradas.
      </p>
    );
  return (
    <div className="table-responsive">
      <table className="table table-bordered mb-2" style={{ fontSize: 14 }}>
        <thead className="thead-light">
          <tr style={{ fontWeight: 600 }}>
            <th>Tipo de recurso</th>
            <th className="text-center">A tiempo</th>
            <th className="text-center">Tardío</th>
            <th className="text-center">Total sesiones</th>
            <th className="text-center" style={{ width: 140 }}>% En plazo</th>
            <th className="text-center" style={{ width: 110 }}>Estado</th>
          </tr>
        </thead>
        <tbody>
          {recursos.map((r, i) => {
            if (r.no_aplica) {
              return (
                <tr key={i} style={{ opacity: 0.4 }}>
                  <td className="align-middle text-muted" style={{ fontWeight: 500 }}>{r.nombre}</td>
                  <td colSpan={5} className="text-center align-middle text-muted" style={{ fontSize: 12, fontStyle: 'italic' }}>
                    No aplica este período
                  </td>
                </tr>
              );
            }
            const v   = Math.round((r.en_fecha / r.total) * 100);
            const col = colorSem(v);
            return (
              <tr key={i}>
                <td className="align-middle" style={{ fontWeight: 500 }}>{r.nombre}</td>
                <td className="text-center align-middle text-success">{r.en_fecha}</td>
                <td className="text-center align-middle text-danger">{r.tarde}</td>
                <td className="text-center align-middle text-muted">{r.total}</td>
                <td className="text-center align-middle">
                  <div style={{ fontWeight: 700, fontSize: 18, color: col }}>{v}%</div>
                  <div className="progress mt-1" style={{ height: 4, borderRadius: 2 }}>
                    <div className="progress-bar" style={{ width: `${Math.min(100, v)}%`, backgroundColor: col, borderRadius: 2 }} />
                  </div>
                </td>
                <td className="text-center align-middle">
                  <span style={{ fontSize: 12, fontWeight: 600, color: col, backgroundColor: `${col}15`, padding: '3px 10px', borderRadius: 20 }}>
                    {labelSem(v)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <small className="text-muted">
        Datos del mes en curso. Para ver detalle por sesión, visita{' '}
        <a href="/reportes-academicos/kpis" style={{ color: '#007bff' }}>Reporte KPI Académico</a>.
      </small>
    </div>
  );
};

// ── ModalDetalle ──────────────────────────────────────────────────────────────
const ModalDetalle = ({ show, onClose, tipo, calidad, recaudacion, fidelizacion }) => {
  const titulo = {
    calidad:     'Detalle por sesión — Calidad',
    retencion:   'Detalle por producto — Retención',
    recaudacion: 'Detalle de períodos — Recaudación',
  }[tipo] || '';

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton style={{ padding: '12px 16px' }}>
        <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>{titulo}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: 16 }}>

        {/* ── Calidad: tabla por sesión ── */}
        {tipo === 'calidad' && (calidad || []).map((proy, j) => {
          const sesiones = proy.datos_sesiones || [];
          return (
            <div key={j} className={j < calidad.length - 1 ? 'mb-4' : ''}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: '#444' }}>
                {proy.nombre_proyecto}
                <small className="text-muted font-weight-normal ml-2" style={{ fontSize: 12 }}>
                  {proy.fecha_inicio} — {proy.fecha_fin}
                </small>
              </div>
              {sesiones.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-bordered table-sm mb-0" style={{ fontSize: 13 }}>
                    <thead className="thead-light">
                      <tr>
                        <th>Sesión</th>
                        <th className="text-center">Fecha</th>
                        <th className="text-center">Docente %</th>
                        <th className="text-center">Encuestas D.</th>
                        <th className="text-center">Tutor %</th>
                        <th className="text-center">Encuestas T.</th>
                        <th className="text-center">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sesiones.map((s, i) => {
                        const avgV = ((s.doc_pct ?? 0) + (s.tutor_pct ?? 0)) / ((s.doc_pct !== null ? 1 : 0) + (s.tutor_pct !== null ? 1 : 0) || 1);
                        const col = colorSem(avgV);
                        return (
                          <tr key={i}>
                            <td className="align-middle">{s.sesion}</td>
                            <td className="text-center align-middle text-muted" style={{ fontSize: 12 }}>{s.fecha || '—'}</td>
                            <td className="text-center align-middle">
                              {s.doc_pct !== null
                                ? <span style={{ fontWeight: 700, color: colorSem(s.doc_pct) }}>{s.doc_pct}%</span>
                                : <span className="text-muted">—</span>
                              }
                            </td>
                            <td className="text-center align-middle text-muted">{s.doc_n || '—'}</td>
                            <td className="text-center align-middle">
                              {s.tutor_pct !== null
                                ? <span style={{ fontWeight: 700, color: colorSem(s.tutor_pct) }}>{s.tutor_pct}%</span>
                                : <span className="text-muted">—</span>
                              }
                            </td>
                            <td className="text-center align-middle text-muted">{s.tutor_n || '—'}</td>
                            <td className="text-center align-middle">
                              <span style={{ fontSize: 11, fontWeight: 600, color: col, backgroundColor: `${col}15`, padding: '2px 8px', borderRadius: 20 }}>
                                {labelSem(avgV)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted mb-0" style={{ fontSize: 13 }}>Sin detalle por sesión disponible.</p>
              )}
            </div>
          );
        })}

        {/* ── Retención: tabla completa ── */}
        {tipo === 'retencion' && (
          <div className="table-responsive">
            <table className="table table-bordered mb-0" style={{ fontSize: 13 }}>
              <thead className="thead-light">
                <tr>
                  <th>Producto</th>
                  <th className="text-center">Matriculados</th>
                  <th className="text-center">Activos</th>
                  <th className="text-center">Culminados</th>
                  <th className="text-center">Desertados</th>
                  <th className="text-center">Afiliados</th>
                  <th className="text-center">Brecha</th>
                  <th className="text-center">% Retención</th>
                  <th className="text-center">Estado</th>
                </tr>
              </thead>
              <tbody>
                {(fidelizacion || []).map((p, i) => {
                  const v   = safePct(p.producto_pr);
                  const col = colorSem(v);
                  return (
                    <tr key={i}>
                      <td className="align-middle" style={{ fontWeight: 500 }}>{p.nombre_proyecto}</td>
                      <td className="text-center align-middle">{p.cantidad}</td>
                      <td className="text-center align-middle">{p.activos}</td>
                      <td className="text-center align-middle">{p.culminados}</td>
                      <td className="text-center align-middle text-danger">{p.desertados}</td>
                      <td className="text-center align-middle">{p.afiliados}</td>
                      <td className="text-center align-middle text-muted">{safePct(p.brecha_pr)}%</td>
                      <td className="text-center align-middle">
                        <span style={{ fontWeight: 700, color: col }}>{v}%</span>
                      </td>
                      <td className="text-center align-middle">
                        <span style={{ fontSize: 11, fontWeight: 600, color: col, backgroundColor: `${col}15`, padding: '2px 8px', borderRadius: 20 }}>
                          {labelSem(v)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Recaudación: links descarga ── */}
        {tipo === 'recaudacion' && (
          <div>
            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
              Accede a los reportes detallados de recaudación con el listado completo de alumnos y pagos:
            </p>
            {(recaudacion || []).map((dato, i) => (
              <div key={i} className="mb-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: 8 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>
                  {dato.fecha_inicio} — {dato.fecha_fin}
                  <span style={{ marginLeft: 12, fontWeight: 700, color: colorSem(safePct(dato?.pagado?.porcentaje)), fontSize: 16 }}>
                    {safePct(dato?.pagado?.porcentaje)}% cobrado
                  </span>
                </div>
                <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                  {dato.enlaceMeta && (
                    <a href={dato.enlaceMeta} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                      <FontAwesomeIcon icon={faFileExcel} className="mr-1" />
                      Detalle Programado
                    </a>
                  )}
                  {dato.enlacePerdida && (
                    <a href={dato.enlacePerdida} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-danger">
                      <FontAwesomeIcon icon={faFileExcel} className="mr-1" />
                      Detalle Pérdida
                    </a>
                  )}
                  {dato.enlaceRecuperacion && (
                    <a href={dato.enlaceRecuperacion} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-success">
                      <FontAwesomeIcon icon={faFileExcel} className="mr-1" />
                      Detalle Recuperación
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

      </Modal.Body>
      <Modal.Footer style={{ padding: '8px 16px' }}>
        <Button variant="secondary" size="sm" onClick={onClose}>Cerrar</Button>
      </Modal.Footer>
    </Modal>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const ReporteNaranja = () => {
  const dispatch = useDispatch();
  const { loading, error, reporteNaranja } = useSelector(s => s.reporteNaranja);
  const datosAcad = useSelector(s => s.kpisAcademicos.datos);

  const now = new Date();
  const initialValues = {
    proyectoSeleccionado: [],
    fechaInicio: new Date(now.getFullYear(), now.getMonth(), 1),
    fechaFin:    new Date(now.getFullYear(), now.getMonth() + 1, 1),
  };
  const [datos, setDatos] = useState(initialValues);
  const [filtroBloque, setFiltroBloque] = useState('Todos');
  const [modal, setModal] = useState({ show: false, tipo: null });

  useEffect(() => {
    dispatch(obtenerProyectosActivos()).then(proyectos => {
      if (proyectos) setDatos(d => ({ ...d, proyectoSeleccionado: proyectos }));
    });
    dispatch(obtenerKpisAcademicos(now.getFullYear(), now.getMonth() + 1));
  }, []);

  useEffect(() => {
    dispatch(limpiarResultadoBusqueda());
    dispatch(obtenerReporteNaranja(datos));
  }, [datos]);

  const recaudacion  = reporteNaranja?.recaudacion   || [];
  const calidad      = reporteNaranja?.calidad        || [];
  const fidelizacion = reporteNaranja?.fidelizacion   || [];
  const recursos     = datosAcad?.recursos            || [];

  // Summary %
  const recPct = recaudacion.length
    ? Math.round(recaudacion.reduce((s, d) => s + safePct(d?.pagado?.porcentaje), 0) / recaudacion.length)
    : 0;
  const calItems = calidad.flatMap(p => p.datos || []);
  const calPct = calItems.length
    ? Math.round(calItems.reduce((s, d) => s + safePct(d.porcentaje_mayor), 0) / calItems.length)
    : 0;
  const retPct = fidelizacion.length
    ? Math.round(fidelizacion.reduce((s, p) => s + safePct(p.producto_pr), 0) / fidelizacion.length)
    : 0;
  const recActivos  = recursos.filter(r => !r.no_aplica && r.total > 0);
  const recAcadPct  = recActivos.length
    ? Math.round(recActivos.reduce((s, r) => s + (r.en_fecha / r.total) * 100, 0) / recActivos.length)
    : 0;

  // Números absolutos para las tarjetas
  const recTotalMeta = recaudacion.reduce((s, d) => s + parseFloat(d?.meta?.monto || 0), 0);
  const recTotalCob  = recaudacion.reduce((s, d) => s + parseFloat(d?.pagado?.monto || 0), 0);
  const calCumplen   = calItems.filter(d => safePct(d.porcentaje_mayor) >= META).length;
  const retActivos   = fidelizacion.reduce((s, p) => s + parseInt(p.activos || 0) + parseInt(p.culminados || 0), 0);
  const retTotal     = fidelizacion.reduce((s, p) => s + parseInt(p.cantidad || 0), 0);
  const recEnFecha   = recActivos.reduce((s, r) => s + (r.en_fecha || 0), 0);
  const recTotalSes  = recActivos.reduce((s, r) => s + (r.total || 0), 0);

  const hayDatos = recaudacion.length > 0 || calidad.length > 0 || fidelizacion.length > 0;

  const mostrar = (bloque) => filtroBloque === 'Todos' || filtroBloque === bloque;

  const handleBuscar = d => {
    dispatch(limpiarResultadoBusqueda());
    dispatch(obtenerReporteNaranja(d));
  };

  // ── Export Excel ──────────────────────────────────────────────────────────
  const exportarExcel = () => {
    const wb = XLSX.utils.book_new();
    const estado = v => v >= META ? 'Cumple' : v >= 80 ? 'En riesgo' : 'No cumple';

    // Hoja resumen
    const wsResumen = XLSX.utils.aoa_to_sheet([
      ['KPI', '%', 'Meta', 'Estado'],
      ['Recaudación',         recPct,    META + '%', estado(recPct)],
      ['Calidad docente',     calPct,    META + '%', estado(calPct)],
      ['Retención',           retPct,    META + '%', estado(retPct)],
      ['Recursos 3 días',     recAcadPct, META + '%', estado(recAcadPct)],
    ]);
    XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen');

    // Hoja recaudación
    if (recaudacion.length) {
      const wsRec = XLSX.utils.aoa_to_sheet([
        ['Período', 'Programado (S/.)', 'Cobrado (S/.)', 'Pendiente (S/.)', '% Cobrado', 'Estado'],
        ...recaudacion.map(d => [
          `${d.fecha_inicio} – ${d.fecha_fin}`,
          d.meta?.monto || 0,
          d.pagado?.monto || 0,
          d.pendiente?.monto || 0,
          safePct(d.pagado?.porcentaje) + '%',
          estado(safePct(d.pagado?.porcentaje)),
        ]),
      ]);
      XLSX.utils.book_append_sheet(wb, wsRec, 'Recaudación');
    }

    // Hoja calidad
    if (calidad.length) {
      const filas = [['Producto', 'Fecha Inicio', 'Fecha Fin', 'Tipo', 'Sesión', 'Doc. %', 'Tutor %', 'Estado']];
      calidad.forEach(p => {
        (p.datos_sesiones || []).forEach(s => {
          const avg = ((s.doc_pct ?? 0) + (s.tutor_pct ?? 0)) / ((s.doc_pct !== null ? 1 : 0) + (s.tutor_pct !== null ? 1 : 0) || 1);
          filas.push([p.nombre_proyecto, p.fecha_inicio, p.fecha_fin, 'Sesión', s.sesion, s.doc_pct ?? '—', s.tutor_pct ?? '—', estado(avg)]);
        });
        (p.datos || []).forEach(d => {
          filas.push([p.nombre_proyecto, p.fecha_inicio, p.fecha_fin, d.tema, 'Promedio', safePct(d.porcentaje_mayor), '—', estado(safePct(d.porcentaje_mayor))]);
        });
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(filas), 'Calidad');
    }

    // Hoja retención
    if (fidelizacion.length) {
      const wsRet = XLSX.utils.aoa_to_sheet([
        ['Producto', 'Matriculados', 'Activos', 'Culminados', 'Desertados', 'Afiliados', '% Retención', 'Estado'],
        ...fidelizacion.map(p => [
          p.nombre_proyecto,
          p.cantidad, p.activos, p.culminados, p.desertados, p.afiliados,
          safePct(p.producto_pr) + '%',
          estado(safePct(p.producto_pr)),
        ]),
      ]);
      XLSX.utils.book_append_sheet(wb, wsRet, 'Retención');
    }

    XLSX.writeFile(wb, `KPIs_Naranja_${now.toISOString().slice(0, 10)}.xlsx`);
  };

  // ── Export PDF ────────────────────────────────────────────────────────────
  const exportarPdf = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const estado = v => v >= META ? 'Cumple' : v >= 80 ? 'En riesgo' : 'No cumple';

    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text("Reporte KPI'S — INEDI Posgrados", 14, 18);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Generado: ${now.toLocaleDateString('es-PE')}`, 14, 25);

    doc.autoTable({
      startY: 30,
      head: [['KPI', '%', 'Meta', 'Estado']],
      body: [
        ['Recaudación',     recPct + '%',     META + '%', estado(recPct)],
        ['Calidad docente', calPct + '%',     META + '%', estado(calPct)],
        ['Retención',       retPct + '%',     META + '%', estado(retPct)],
        ['Recursos 3 días', recAcadPct + '%', META + '%', estado(recAcadPct)],
      ],
      styles: { fontSize: 10 },
      headStyles: { fillColor: [52, 73, 94] },
    });

    if (recaudacion.length) {
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Período', 'Programado', 'Cobrado', 'Pendiente', '% Cobrado', 'Estado']],
        body: recaudacion.map(d => [
          `${d.fecha_inicio} – ${d.fecha_fin}`,
          'S/. ' + (d.meta?.monto || 0),
          'S/. ' + (d.pagado?.monto || 0),
          'S/. ' + (d.pendiente?.monto || 0),
          safePct(d.pagado?.porcentaje) + '%',
          estado(safePct(d.pagado?.porcentaje)),
        ]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [41, 128, 185] },
        didDrawPage: (data) => {
          if (data.pageNumber === 1) {
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text('Recaudación', 14, doc.lastAutoTable.finalY + 14);
          }
        },
      });
    }

    if (fidelizacion.length) {
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Producto', 'Matriculados', 'Activos', 'Culminados', 'Desertados', '% Retención', 'Estado']],
        body: fidelizacion.map(p => [
          p.nombre_proyecto, p.cantidad, p.activos, p.culminados,
          p.desertados, safePct(p.producto_pr) + '%', estado(safePct(p.producto_pr)),
        ]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [39, 174, 96] },
      });
    }

    doc.save(`KPIs_Naranja_${now.toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <Layout>
      <HeaderContent title="Reporte KPI'S" />
      <Breadcrumb link={[
        { url: '/inicio', nombre: 'Inicio', icon: 'fas fa-home' },
        { url: '', nombre: 'Reportes' },
        { url: '', nombre: "KPI'S" },
      ]} />

      {/* Filtros */}
      <div className="box p-3 mb-3">
        <FormularioFiltroReporte
          datosIniciales={datos}
          onSubmit={handleBuscar}
          setValores={() => {}}
          ingresos
        />
      </div>

      <SpinnerLoading loading={loading} error={error}>
        {hayDatos ? (
          <>
            {/* Tarjetas resumen */}
            <div className="row mb-2">
              <SummaryCard titulo="Recaudación"     valor={recPct}     icon={faDollarSign}
                subtitulo="% de pagos cobrados en el período"
                detalle={recTotalMeta > 0 ? `S/. ${recTotalCob.toLocaleString('es-PE')} cobrado de S/. ${recTotalMeta.toLocaleString('es-PE')}` : null}
              />
              <SummaryCard titulo="Calidad docente" valor={calPct}     icon={faStar}
                subtitulo="% satisfacción ≥ 4/5 por sesión"
                detalle={calItems.length > 0 ? `${calCumplen} de ${calItems.length} evaluaciones cumplen meta` : null}
              />
              <SummaryCard titulo="Retención"       valor={retPct}     icon={faUsers}
                subtitulo="% alumnos activos vs matriculados"
                detalle={retTotal > 0 ? `${retActivos} de ${retTotal} alumnos activos o culminados` : null}
              />
              <SummaryCard titulo="Recursos 3 días" valor={recAcadPct} icon={faClipboardCheck}
                subtitulo="Recursos subidos a tiempo (mes actual)"
                detalle={recTotalSes > 0 ? `${recEnFecha} de ${recTotalSes} sesiones a tiempo` : null}
              />
            </div>

            {/* Banner de alertas */}
            {(() => {
              const problemas = [
                recPct < META    && { kpi: 'Recaudación',     v: recPct,     nivel: recPct < 80    ? 'danger' : 'warning' },
                calPct < META    && { kpi: 'Calidad docente', v: calPct,     nivel: calPct < 80    ? 'danger' : 'warning' },
                retPct < META    && { kpi: 'Retención',       v: retPct,     nivel: retPct < 80    ? 'danger' : 'warning' },
                recAcadPct < META && { kpi: 'Recursos 3 días', v: recAcadPct, nivel: recAcadPct < 80 ? 'danger' : 'warning' },
              ].filter(Boolean);
              if (!problemas.length) return null;
              return (
                <div className="mb-3">
                  {problemas.map((p, i) => (
                    <div key={i}
                      className="d-flex align-items-center py-2 px-3 mb-1"
                      style={{
                        borderRadius: 6, fontSize: 13,
                        backgroundColor: p.nivel === 'danger' ? '#fff5f5' : '#fffbf0',
                        borderLeft: `4px solid ${p.nivel === 'danger' ? '#dc3545' : '#fd7e14'}`,
                        color: p.nivel === 'danger' ? '#8b1a1a' : '#7a4100',
                      }}
                    >
                      <FontAwesomeIcon
                        icon={p.nivel === 'danger' ? faTimesCircle : faExclamationTriangle}
                        className="mr-2"
                        style={{ color: p.nivel === 'danger' ? '#dc3545' : '#fd7e14', flexShrink: 0 }}
                      />
                      <span>
                        <strong>{p.kpi}</strong>{' '}
                        {p.nivel === 'danger' ? 'no cumple la meta' : 'en riesgo'}: {p.v}%
                        {' — '}
                        {p.nivel === 'danger'
                          ? `${(META - p.v).toFixed(1)}% por debajo de la meta`
                          : `a ${(META - p.v).toFixed(1)}% de la meta`}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Barra de herramientas */}
            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap" style={{ gap: 10 }}>
              {/* Filtro por tipo de indicador */}
              <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                {['Todos', ...BLOQUES.map(b => BLOQUE_LABEL[b])].map(label => {
                  const key = label === 'Todos' ? 'Todos' : Object.keys(BLOQUE_LABEL).find(k => BLOQUE_LABEL[k] === label);
                  const active = filtroBloque === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setFiltroBloque(key)}
                      className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={{ fontSize: 12, borderRadius: 20, padding: '3px 14px' }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>

              {/* Botones export */}
              <div className="d-flex" style={{ gap: 8 }}>
                <button className="btn btn-sm btn-success" onClick={exportarExcel} style={{ fontSize: 12 }}>
                  <FontAwesomeIcon icon={faFileExcel} className="mr-1" />
                  Excel
                </button>
                <button className="btn btn-sm btn-danger" onClick={exportarPdf} style={{ fontSize: 12 }}>
                  <FontAwesomeIcon icon={faFilePdf} className="mr-1" />
                  PDF
                </button>
              </div>
            </div>

            {/* Bloques de detalle */}
            {mostrar('Recaudacion') && (
              <BloqueKpi
                titulo="Recaudación" valor={recPct} icon={faDollarSign}
                descripcion="Pagos cobrados vs monto programado en el período seleccionado"
                onVerMas={() => setModal({ show: true, tipo: 'recaudacion' })}
              >
                <ResultadoRecaudacion recaudacion={recaudacion} />
              </BloqueKpi>
            )}

            {mostrar('Calidad') && (
              <BloqueKpi
                titulo="Calidad docente" valor={calPct} icon={faStar}
                descripcion="Satisfacción de encuestas por sesión (docente y tutor)"
                onVerMas={() => setModal({ show: true, tipo: 'calidad' })}
              >
                <ResultadoCalidad calidad={calidad} />
              </BloqueKpi>
            )}

            {mostrar('Retencion') && (
              <BloqueKpi
                titulo="Retención" valor={retPct} icon={faUsers}
                descripcion="Alumnos activos o culminados vs total matriculados por producto"
                onVerMas={() => setModal({ show: true, tipo: 'retencion' })}
              >
                <ResultadoFidelidad fidelizacion={fidelizacion} />
              </BloqueKpi>
            )}

            {mostrar('Recursos') && (
              <BloqueKpi
                titulo="Recursos completos 3 días antes" valor={recAcadPct} icon={faClipboardCheck}
                descripcion="Materiales académicos subidos al menos 3 días antes de cada sesión"
              >
                <RecursosBlock recursos={recursos} />
              </BloqueKpi>
            )}
          </>
        ) : (
          <div className="box p-5 text-center">
            <FontAwesomeIcon
              icon={faChartBar}
              style={{ fontSize: 48, opacity: 0.15, display: 'block', margin: '0 auto 16px' }}
            />
            <p className="text-muted mb-0">
              Selecciona un rango de fechas y haz clic en Buscar para ver los KPIs.
            </p>
          </div>
        )}
      </SpinnerLoading>

      {/* Modal Ver más */}
      <ModalDetalle
        show={modal.show}
        onClose={() => setModal({ show: false, tipo: null })}
        tipo={modal.tipo}
        calidad={calidad}
        recaudacion={recaudacion}
        fidelizacion={fidelizacion}
      />
    </Layout>
  );
};

export default ReporteNaranja;
